// Patterns — the Problem-Solving Playbook. Recurring leadership binds drawn
// from the residential, each as Situation → Pattern → Move → Watch-out, linked
// to the framework(s) behind it. Synthesised from the programme materials.

export interface Pattern {
  id: string;
  title: string;
  theme: string;
  faculty: string;
  situation: string;
  pattern: string;
  move: string;
  watchOut: string;
  frameworks: string[];
}

export const PATTERNS: Pattern[] = [
  {
    id: "wrong-response",
    title: "The wrong response to the wrong problem",
    theme: "Culture",
    faculty: "Sue Dopson",
    situation: "A complex, no-known-answer challenge lands and you feel pressure to act decisively.",
    pattern: "You reach for command — providing the answer — on a wicked problem that only collective sense-making can move.",
    move: "Name the problem type first; on a wicked problem lead by asking questions and convening the people who must wrestle with it.",
    watchOut: "A withheld point in a meeting isn't only a confidence issue — it can be a failure to apply the right leadership response to a complex problem.",
    frameworks: ["grint-problems", "ego-eco"],
  },
  {
    id: "culture-by-exhortation",
    title: "Changing culture by exhortation",
    theme: "Culture",
    faculty: "Sue Dopson",
    situation: "You want to shift 'the culture' and launch a values campaign to do it.",
    pattern: "You exhort people and publish values while leaving measurement, hiring and structure untouched — so the system quietly pulls the change back.",
    move: "Change what the system attends to and rewards: the structural levers (metrics, resourcing, promotion) move culture, not posters.",
    watchOut: "When a programme prescribes individual behaviour change for a systemic problem, Schein's mechanisms are your counter-evidence.",
    frameworks: ["schein-mechanisms", "congruence"],
  },
  {
    id: "single-point-change",
    title: "The change that quietly reverts",
    theme: "Culture",
    faculty: "Sue Dopson",
    situation: "A new way of working lands well, then fades within a quarter.",
    pattern: "You moved one element (culture) while people, process and structure stayed aligned to the old way and dragged it back.",
    move: "Move at least one structural element alongside any behavioural ask, so the system reinforces the change rather than resisting it.",
    watchOut: "When something reverts, look for the misaligned incentive or process before blaming people's commitment.",
    frameworks: ["congruence", "schein-mechanisms"],
  },
  {
    id: "reflexive-bothand",
    title: "The reflexive both/and (and the false compromise)",
    theme: "Holding tension",
    faculty: "Dr Chris Moos",
    situation: "A tension shows up and you want to handle it well.",
    pattern: "You either split the difference into a value-destroying compromise, or reach for the hard both/and on every trivial trade-off.",
    move: "Place the tension on importance × capacity; reserve both/and for tensions that genuinely matter and where you have leverage, and resolve the rest cleanly.",
    watchOut: "The comfortable middle that satisfies nobody is the most common trap — name it as a false compromise.",
    frameworks: ["tension-grid", "paradox"],
  },
  {
    id: "invite-then-punish",
    title: "Inviting voice, then punishing it",
    theme: "Psychological safety",
    faculty: "Amanda Gethi",
    situation: "You tell the team to speak up, and someone does.",
    pattern: "Your face, tone or structural response in the next thirty seconds tells everyone the invitation wasn't real, and they edit within minutes.",
    move: "Treat what's shared as intelligence — thank them, acknowledge the difficulty, and act on it; the work is the response, not the invitation.",
    watchOut: "Your expression in the first two seconds after someone speaks decides whether they, and everyone watching, will risk it again.",
    frameworks: ["clark", "truth"],
  },
  {
    id: "silence-as-agreement",
    title: "Mistaking silence for agreement",
    theme: "Psychological safety",
    faculty: "Amanda Gethi",
    situation: "You ask 'any questions?' and get none — but the real debate happens afterwards at the water cooler.",
    pattern: "You read silence as consent rather than as unsafety, or as cultural deference you haven't accounted for.",
    move: "Find the voices you wouldn't normally hear; ask, don't wait — if you don't know what an introvert thinks, you haven't asked.",
    watchOut: "The real intelligence usually hides in the mildly embarrassing, career-risky, counter-cultural topics no one volunteers.",
    frameworks: ["clark", "conversation-types"],
  },
  {
    id: "optimism-bubble",
    title: "The optimism bubble",
    theme: "Courage & candour",
    faculty: "Dr Megan Reitz",
    situation: "You're senior and speaking up feels like it works fine around here.",
    pattern: "You rate yourself by intent and others by behaviour, and over-estimate voice, approachability and your own listening — because it does work fine for you.",
    move: "Assume the gap operates in you, not just them; run TRUTH on yourself first, and treat the absence of bad news as a question.",
    watchOut: "Advantage blindness means the labels that get you heard are exactly the ones you've stopped noticing.",
    frameworks: ["three-traps", "truth"],
  },
  {
    id: "door-always-open",
    title: "'My door is always open'",
    theme: "Courage & candour",
    faculty: "Dr Megan Reitz",
    situation: "You've told everyone you're approachable and your door is open.",
    pattern: "You believe people will walk through it, despite the labels that make you intimidating and a calendar that signals the opposite.",
    move: "Ask who finds you a little intimidating and do the small things that lower the threshold — a pause at the door, going second, time that looks free.",
    watchOut: "Some intimidation you can't remove; the error is concluding that because you can't fix all of it, you needn't do the part you can.",
    frameworks: ["three-traps", "space"],
  },
  {
    id: "echo-chamber",
    title: "The echo-chamber list",
    theme: "Courage & candour",
    faculty: "Dr Megan Reitz",
    situation: "You have a handful of trusted people you go to for a sense-check.",
    pattern: "Your advice list has quietly filled with people who sound like you — same function, tenure, language — and you've built an echo chamber without noticing.",
    move: "List your top five, look honestly for difference, and deliberately add one dissimilar voice with a real reason to talk this month.",
    watchOut: "Network node-points are influential regardless of seniority; a different name on paper that you never actually weight changes nothing.",
    frameworks: ["three-traps", "truth"],
  },
  {
    id: "task-suffocates",
    title: "Task suffocates the other conversations",
    theme: "Courage & candour",
    faculty: "Dr Megan Reitz",
    situation: "Your meetings are full and relentlessly about delivery.",
    pattern: "Task crowds out the relational, reflective, creative and values conversations — and the language for non-task work ('non-productive time') gives the game away.",
    move: "Audit one meeting across the five conversation types and make a structural change to design room for a starved one.",
    watchOut: "Treating it as a personality problem — exhorting people to be more reflective — when it's the meeting design that's starving them.",
    frameworks: ["conversation-types", "doing-spacious"],
  },
  {
    id: "doing-mode",
    title: "Busy thoughtlessness",
    theme: "Attention",
    faculty: "Dr Megan Reitz",
    situation: "You're relentlessly busy and delivering, but unsure the activity is the right activity.",
    pattern: "You live in doing mode; spacious mode reads as indulgent or invisible, so you never step back until a crisis forces it.",
    move: "Defend spacious time on purpose, in conditions you control, before the questions arrive as an emergency.",
    watchOut: "'If you open up space, you let doubt in' is the honest reason it's avoided — and exactly why it matters.",
    frameworks: ["doing-spacious", "space"],
  },
  {
    id: "filterworld",
    title: "Over-served by the algorithm",
    theme: "Curiosity",
    faculty: "Dr Oliver Cox",
    situation: "Your information diet is curated — feeds, digests, the usual voices.",
    pattern: "Inputs reach you because they're calibrated to people like you, so your view of the world flattens to the least demanding version of it.",
    move: "Engineer one encounter a week with an un-served voice — a customer, a junior, a different function — in their own words, not a summary.",
    watchOut: "Algorithmic curation and your trusted advice list are the same flattening at different scales.",
    frameworks: ["close-looking", "three-traps"],
  },
  {
    id: "obsolete-as-permanent",
    title: "Treating the obsolete as permanent",
    theme: "Curiosity",
    faculty: "Dr Oliver Cox",
    situation: "A system everyone relies on is treated as a fixed fact of life.",
    pattern: "The pivot is obvious in hindsight but nearly invisible at the time, and your decisions quietly assume the old system will persist.",
    move: "Name the system in your patch that won't survive five years, and ask what changes now if you take that seriously.",
    watchOut: "What looks permanent is often just unexamined — the horses looked permanent in 1919 too.",
    frameworks: ["close-looking", "picl"],
  },
  {
    id: "unspoken-fix",
    title: "The unspoken fix — 'nobody ever asked'",
    theme: "Conversations",
    faculty: "John Denton",
    situation: "A persistent operational problem drags on while the person closest to it stays quiet.",
    pattern: "Leadership reaches for experts and decks instead of the person on the floor, who has held the fix for years because nobody ever asked.",
    move: "Go to where the work happens, be genuinely curious, ask a lot of questions, listen to the answer, then act.",
    watchOut: "The cost of an unasked question compounds silently — sometimes for years.",
    frameworks: ["advocacy-enquiry", "truth"],
  },
  {
    id: "advocacy-no-enquiry",
    title: "Advocacy without enquiry",
    theme: "Conversations",
    faculty: "John Denton",
    situation: "A team member brings you a problem.",
    pattern: "You default to telling and advising — solving it for them — because push is the habitual mode for most managers.",
    move: "Replace a piece of advice with a genuine question and wait; give the work back to the person who owns it.",
    watchOut: "A leading question is still push, and people hear it as such.",
    frameworks: ["advocacy-enquiry", "grip"],
  },
  {
    id: "change-on-change",
    title: "Change on change",
    theme: "Leading change",
    faculty: "John Denton",
    situation: "You've taken a team into the performance dip the change curve guarantees.",
    pattern: "Someone impatient says 'this isn't working' and launches another change mid-dip, so you fall down the curve again — decline dressed up as continuous improvement.",
    move: "See the first change through and let performance stabilise — measured by output and by whether people have stopped talking about the past — before starting the next.",
    watchOut: "People need time (often a year or more); warn your own leadership that a dip is coming so they don't pull the plug.",
    frameworks: ["change-curve", "change-equation"],
  },
  {
    id: "same-symptom",
    title: "Same symptom, opposite cause",
    theme: "Leading change",
    faculty: "John Denton",
    situation: "You ask for a change and nothing happens.",
    pattern: "You pattern-match one fix to the symptom, but 'nothing happens' can come from perceived loss of power or from perceived threat — which need opposite responses.",
    move: "Diagnose symptom → cause → solution before prescribing; the tidy engineer's fix applied without diagnosis backfires.",
    watchOut: "In the absence of communication, people assume the worst — and the worst guess is usually wrong.",
    frameworks: ["change-curve", "change-equation"],
  },
  {
    id: "sheep-in-cold",
    title: "Walking the paper in cold",
    theme: "Influence & politics",
    faculty: "John Denton",
    situation: "You have a good proposal and the right intentions.",
    pattern: "You present it cold to the decision-makers expecting them to do the right thing — the sheep walking in like a lamb to the slaughter.",
    move: "Socialise the paper first: walk it round each character before the meeting, pitched to suit them, and let the owl carry the trickiest conversation.",
    watchOut: "Don't get cornered into a private deal with the fox, and don't mistake the donkey's confidence for awareness.",
    frameworks: ["baddeley", "stakeholder-map"],
  },
  {
    id: "default-to-compete",
    title: "Defaulting to compete",
    theme: "Thriving teams",
    faculty: "John Denton",
    situation: "A situation where collaboration would produce the better collective result.",
    pattern: "Under mild pressure and an ambiguous brief, people compete by default — even after being primed to collaborate.",
    move: "Ask explicitly: is this a moment to compete or collaborate? Reframe to grow the pie rather than split it.",
    watchOut: "When the frame is ambiguous, people compete unless something in the system makes collaboration the obvious move.",
    frameworks: ["grip", "trust-equation"],
  },
  {
    id: "ambiguous-brief",
    title: "Building before you understand the brief",
    theme: "Thriving teams",
    faculty: "John Denton",
    situation: "A boss or customer hands you a requirement that sounds clear.",
    pattern: "You start building before you understand what's actually being asked — 'fastest possible' gets read four different ways.",
    move: "You cannot ask too many questions; take the outside view and check the benchmark before you start.",
    watchOut: "Most teams build before knowing the benchmark — and knowing it would have changed everything.",
    frameworks: ["grip", "advocacy-enquiry"],
  },
  {
    id: "diminishing-language",
    title: "The polite undercut",
    theme: "Voice & presence",
    faculty: "Eve Tomlinson",
    situation: "You want to make a point but stay polite.",
    pattern: "You front-load hedges and apologies that shrink you ('I just', 'I'm not an expert but', 'sorry') or shrink the claim ('maybe', 'hopefully') before the content lands.",
    move: "Delete the hedge — the deletion is the power. It's 'I have a dream', not 'I'd like to tell you about my dream'.",
    watchOut: "This is the linguistic fingerprint of upward silence: Reitz tells you why you self-censor, Tomlinson shows you the words.",
    frameworks: ["fear", "truth"],
  },
  {
    id: "individual-fix-systemic",
    title: "An individual fix for a systemic problem",
    theme: "Leading change",
    faculty: "Across faculty",
    situation: "You're 'not being heard', or you withheld a substantive point.",
    pattern: "The advice prescribes an individual fix — be braver, more charismatic, more assertive — for what is actually a structural or relational condition.",
    move: "Turn each tool on yourself first, test whether the feared reaction is fantasised or evidenced, take the micro-act anyway — and name the structural lever you'd change so the next person doesn't have to be brave.",
    watchOut: "Charisma treats the wrong organ when the real variable was your forecast of someone else's reaction.",
    frameworks: ["fear", "truth", "schein-mechanisms"],
  },
];

export const PATTERN_THEMES = [
  "Culture",
  "Holding tension",
  "Psychological safety",
  "Courage & candour",
  "Attention",
  "Curiosity",
  "Conversations",
  "Leading change",
  "Influence & politics",
  "Thriving teams",
  "Voice & presence",
];
